import React, { useState } from 'react';
import { styled } from '@mui/material';
import { Button } from 'components/Button/Button';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { hasRecentHelpRequest } from 'features/signUp/utils/helpRequestUtils';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { SignupStep } from 'features/signUp/types';

const HelpButton = styled(Button)(() => ({
  alignSelf: 'center',
}));

const shouldShowHelpButton = (step: SignupStep): boolean => {
  // Don't show help button on transition steps
  const transitionSteps: SignupStep[] = [
    'AccessCodeMatched',
    'EligibilityVerified',
    'SSOSignUpSuccess',
    'Welcome',
    'SSOSignupLoading',
    'ShortCutSSO',
  ];
  return !transitionSteps.includes(step);
};

interface SignUpHelpButtonProps {
  currentStep: SignupStep;
  openHelpDialog: () => void;
}

export const SignUpHelpButton = ({ currentStep, openHelpDialog }: SignUpHelpButtonProps) => {
  const { translation } = useTranslationContext();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const displayHelpButton = shouldShowHelpButton(currentStep);
  const handleHelpButtonClick = () => {
    if (hasRecentHelpRequest()) {
      setShowConfirmation(true);
    } else {
      openHelpDialog();
    }
  };

  const handleSubmitAnotherRequest = () => {
    setShowConfirmation(false);
    openHelpDialog();
  };

  const handleCancelSubmitRequest = () => {
    setShowConfirmation(false);
  };

  if (!displayHelpButton) {
    return null;
  }

  return (
    <>
      <HelpButton
        className="signup__help-button"
        variant="tertiary"
        startIcon={<HelpOutlineIcon fontSize="small" />}
        aria-label="Help Button"
        onClick={handleHelpButtonClick}
      >
        {translation('help')}
      </HelpButton>
      <ConfirmationDialog
        open={showConfirmation}
        title={translation('help_request_alert')}
        description={translation('help_ticket_submitted_alert_body')}
        handleYes={handleCancelSubmitRequest}
        handleNo={handleSubmitAnotherRequest}
        onClose={handleCancelSubmitRequest}
        yesButtonLabel={translation('ok')}
        noButtonLabel={translation('submit_another_ticket')}
      />
    </>
  );
};
